import { useState } from 'react'
import { ChartShell, formatChartValue, useTweened, type ChartUnit } from './chartKit'

export interface FunnelStep {
  id: string
  label: string
  value: number
}

export interface FunnelChartProps {
  steps: FunnelStep[]
  unit?: ChartUnit | undefined
  loading?: boolean | undefined
  label: string
  emptyTitle?: string | undefined
  emptyDescription?: string | undefined
}

/** Imbuto: una barra centrata per passo, larga in proporzione al primo; sotto ogni passo la quota rispetto al precedente. */
export function FunnelChart(p: FunnelChartProps) {
  const { steps, unit = 'number' } = p
  const first = steps[0]?.value ?? 0
  const [active, setActive] = useState<number | null>(null)
  const tween = useTweened(steps.map(s => s.id), steps.map(s => s.value))
  const rate = (i: number) => (i === 0 ? null : (steps[i - 1]!.value > 0 ? (steps[i]!.value / steps[i - 1]!.value) * 100 : null))
  const rateText = (i: number) => { const r = rate(i); return r == null ? '' : `${formatChartValue(r, 'percent', 1)} del passo precedente` }
  const ofFirst = (i: number) => (first > 0 ? formatChartValue((steps[i]!.value / first) * 100, 'percent', 1) : '—')
  const table = {
    caption: p.label,
    head: ['Passo', 'Valore', 'Dal primo', 'Dal precedente'],
    rows: steps.map((s, i) => ({ key: s.id, cells: [s.label, formatChartValue(s.value, unit), ofFirst(i), rate(i) == null ? '—' : formatChartValue(rate(i), 'percent', 1)] })),
  }
  const summary = `${p.label}. ${steps.map((s, i) => `${s.label}: ${formatChartValue(s.value, unit)}${i ? `, ${rateText(i)}` : ''}`).join('; ')}.`
  const rowH = 76

  return (
    <ChartShell
      loading={p.loading} empty={steps.length === 0 || first <= 0} emptyTitle={p.emptyTitle} emptyDescription={p.emptyDescription}
      ariaLabel={summary} table={table} height={steps.length * rowH} autoHeight vertical
      count={steps.length} active={active} onActive={setActive} onLeave={() => setActive(null)}
      announce={active == null ? '' : `${steps[active]!.label}: ${formatChartValue(steps[active]!.value, unit)}. ${rateText(active)}`}
    >
      {() => (
        <div className="md-funnel" aria-hidden="true">
          {steps.map((s, i) => (
            <div key={s.id} className="md-funnel__row" data-active={active === i}
              onPointerEnter={() => setActive(i)} onPointerDown={() => setActive(i)}>
              <div className="md-funnel__head"><span>{s.label}</span><strong className="md-num">{formatChartValue(s.value, unit)}</strong></div>
              <div className="md-funnel__track">
                <div className="md-funnel__bar" style={{ width: `${Math.min(1, Math.max(0, (tween[i] ?? 0) / first)) * 100}%`, opacity: Math.max(0.45, 1 - i * 0.14) }} />
              </div>
              <div className="md-funnel__rate">{rateText(i) || ' '}</div>
            </div>
          ))}
        </div>
      )}
    </ChartShell>
  )
}
